/**
 * Shared Media-card metadata for exercise results + session media dialog.
 * Keeps EvaluationVideoMediaCard headers / A–B role lines consistent.
 */

import {
  resolveExercisePlayerById,
  resolveViewerTableNumber,
} from "./exerciseViewerTable";
import {
  buildKotMatchVideoRoleLines,
  resolveKotMatchForFeedback,
} from "./kotPlayerMatches";
import {
  findSequenceForFeedbackMsg,
  parseTableNumberFromVideoUrl,
  resolveClipAssignedPlayers,
  resolveEvaluationVideoCameraPosition,
  resolveEvaluationVideoRecordingDate,
  resolveFeedbackSequenceOrder,
  resolveManualAssignmentPlayers,
  resolveManualVideoAssignment,
} from "./evaluationVideoMatching";
import {
  clipCameraFromSources,
  formatCameraSetupLabel,
  cameraSetupFromViewSetup,
  parseCameraSetupFromFilename,
} from "./evaluationCameraSetup";

function parseEvaluationViewSetup(raw) {
  if (raw == null || raw === "") return {};
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

export function isStrokeSlowMoEvaluationVideo(msg) {
  const setup = parseEvaluationViewSetup(msg?.view_setup);
  if (String(setup.clip_kind || "").toLowerCase() === "stroke_slowmo") {
    return true;
  }
  const url = String(msg?.video_message_url || "");
  if (/stroke[_-]?slowmo/i.test(url)) return true;
  return /^stroke_slowmo$/i.test(String(msg?.text_message || "").trim());
}

export function isMaxRallyEvaluationVideo(msg) {
  const setup = parseEvaluationViewSetup(msg?.view_setup);
  if (String(setup.clip_kind || "").toLowerCase() === "max_rally") {
    return true;
  }
  const url = String(msg?.video_message_url || "");
  if (/max[_-]?rally/i.test(url)) return true;
  return /^max_rally$/i.test(String(msg?.text_message || "").trim());
}

/** Coach explanation recorded before the exercise starts (table camera). */
export function isExerciseInstructionEvaluationVideo(msg) {
  const setup = parseEvaluationViewSetup(msg?.view_setup);
  if (String(setup.clip_kind || "").toLowerCase() === "exercise_instruction") {
    return true;
  }
  if (String(setup.source || "").toLowerCase() === "exercise_instruction") {
    return true;
  }
  const url = String(msg?.video_message_url || "");
  if (/exercise[_-]?instruction/i.test(url)) return true;
  return /^exercise_instruction$/i.test(String(msg?.text_message || "").trim());
}

/**
 * Personal-session / full-match exercise recording (not a short autoeval window,
 * not a generated reel). These should get Game points on the web player.
 */
export function isPersonalMatchEvaluationVideo(msg) {
  if (!msg) return false;
  if (
    isStrokeSlowMoEvaluationVideo(msg) ||
    isMaxRallyEvaluationVideo(msg) ||
    isExerciseInstructionEvaluationVideo(msg)
  ) {
    return false;
  }
  const setup = parseEvaluationViewSetup(msg?.view_setup);
  if (setup.kot_match_id != null && String(setup.kot_match_id).trim()) {
    return false;
  }
  if (setup.personal_match === true || setup.personal_match === 1) return true;
  if (String(setup.clip_kind || "").toLowerCase() === "personal_match") {
    return true;
  }
  const url = String(msg?.video_message_url || "");
  if (/training_exercise/i.test(url)) return true;
  const type = String(msg?.message_type || "").toLowerCase();
  if (type && type !== "evaluation") return false;
  const seqRaw = msg?.sequence_number ?? setup.sequence_number;
  const seq = Number(seqRaw);
  if (!(Number.isFinite(seq) && seq > 0)) return true;
  return false;
}

function isActiveRole(role) {
  if (!role) return false;
  const r = String(role).toLowerCase();
  return (
    r === "active" ||
    r === "performance" ||
    r.includes("actif") ||
    r.includes("activo") ||
    r.includes("aktiv") ||
    r.includes("perform")
  );
}

function isSparringRole(role) {
  if (!role) return false;
  const r = String(role).toLowerCase();
  return r === "sparring" || r.includes("sparring") || r.includes("spar");
}

function isBothParticipationRole(role) {
  return String(role || "").toLowerCase() === "both";
}

function formatCameraSetupLabelForMessage(msg, cameraPosition, t) {
  const explicit =
    cameraSetupFromViewSetup(msg?.view_setup) ||
    parseCameraSetupFromFilename(msg?.video_message_url) ||
    cameraPosition;
  if (!explicit) return null;
  const { cameraSetup, cameraOrdinal } = clipCameraFromSources({
    viewSetup: msg?.view_setup,
    filename: msg?.video_message_url,
    cameraPosition,
  });
  return formatCameraSetupLabel(cameraSetup, cameraOrdinal, t);
}

export function formatExerciseTableLabel(tableNumber, t) {
  const n = Number(tableNumber);
  if (!Number.isFinite(n) || n <= 0) return "";
  return (
    t("table_with_number", { number: n, defaultValue: `Table ${n}` }) ||
    `T${n}`
  );
}

export function formatExercisePlayerName(player, playerId) {
  if (player) {
    const alias = player.user_alias || player.player_alias;
    const rosterName = player.player_name || player.name;
    const first = player.user_first_name || player.first_name;
    const last = player.user_last_name || player.last_name;
    const full = [first, last].filter(Boolean).join(" ").trim();
    if (alias) return String(alias).trim();
    if (rosterName) return String(rosterName).trim();
    if (full) return full;
  }
  if (playerId) return `Player ${String(playerId).slice(-6)}`;
  return "—";
}

/** Short role pill: Performance / Sparring / Both. DB value remains "active". */
export function formatParticipationRoleLabel(role, t) {
  if (!role) return null;
  if (isBothParticipationRole(role)) return t("both", "Both");
  if (isActiveRole(role)) {
    return t("performance_role", "Performance");
  }
  if (isSparringRole(role)) return t("sparring", "Sparring");
  const normalized = String(role).trim();
  return normalized
    ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
    : null;
}

/** "Performance by: Alice" for reels / sequence headers when a name is known. */
export function formatPerformanceByLabel(playerName, t) {
  const name = String(playerName || "").trim();
  if (!name) {
    return t("performance_role", "Performance");
  }
  return (
    t("performance_by", "Performance by: {{name}}", {
      name,
      defaultValue: `Performance by: ${name}`,
    }) || `Performance by: ${name}`
  );
}

export function getSequenceSubtypeLabel(seq, localeCode) {
  if (!seq) return "";
  const locKey =
    localeCode === "fr"
      ? "sub_type_name_fr"
      : localeCode === "de"
        ? "sub_type_name_de"
        : localeCode === "es"
          ? "sub_type_name_es"
          : localeCode === "nl"
            ? "sub_type_name_nl"
            : localeCode === "pt"
              ? "sub_type_name_pt"
              : "sub_type_name_en";
  return (
    seq[locKey] ||
    seq.sub_type_name_en ||
    seq.sub_type_name_fr ||
    seq.sub_type_name ||
    seq.sub_type_ex?.[locKey] ||
    seq.sub_type_ex?.sub_type_name_en ||
    ""
  );
}

export function buildExercisePlayerContext(exercise, fullExercise) {
  const merged = { ...(fullExercise || {}), ...(exercise || {}) };
  return {
    ...merged,
    players: exercise?.players?.length
      ? exercise.players
      : fullExercise?.players,
    player1: exercise?.player1 ?? fullExercise?.player1,
    player2: exercise?.player2 ?? fullExercise?.player2,
    player_details: exercise?.player_details?.length
      ? exercise.player_details
      : fullExercise?.player_details,
    sequences: exercise?.sequences?.length
      ? exercise.sequences
      : fullExercise?.sequences,
  };
}

function resolveSequenceSlotIdentityIds(seq, position, exerciseContext) {
  if (!seq || !position) return [];
  const key = String(position).toUpperCase();
  const idKey =
    key === "A"
      ? "player_a_id"
      : key === "B"
        ? "player_b_id"
        : key === "C"
          ? "player_c_id"
          : key === "D"
            ? "player_d_id"
            : null;
  if (!idKey) return [];
  const ids = [];
  const slotId = seq[idKey];
  if (slotId != null) ids.push(String(slotId));
  const player = resolveExercisePlayerById(exerciseContext, slotId);
  if (player?.player_id != null) ids.push(String(player.player_id));
  if (player?.user_id != null) ids.push(String(player.user_id));
  return [...new Set(ids.filter(Boolean))];
}

function roleLineFromAssignedEntry(
  entry,
  exerciseContext,
  seq,
  viewerIdentityIds,
  t
) {
  const player =
    resolveExercisePlayerById(exerciseContext, entry.playerId) ||
    resolveExercisePlayerById(exerciseContext, entry.userId);
  const roleBySlot = {
    A: seq?.player_a_role || "active",
    B: seq?.player_b_role || "sparring",
    C: seq?.player_c_role || null,
    D: seq?.player_d_role || null,
  };
  const role = entry.role || roleBySlot[entry.slot] || null;
  const roleLabel = formatParticipationRoleLabel(role, t);
  const ids = [
    entry.playerId,
    entry.userId,
    player?.player_id,
    player?.user_id,
  ]
    .filter(Boolean)
    .map(String);
  const name =
    (entry.displayName && String(entry.displayName).trim()) ||
    formatExercisePlayerName(player, entry.playerId || entry.userId);
  return {
    slot: entry.slot,
    name,
    role: roleLabel,
    isViewer: viewerIdentityIds.some((id) => ids.includes(String(id))),
    isActive: isActiveRole(role),
    isSparring: isSparringRole(role),
  };
}

function buildSequencePlayerRoleLines(seq, exerciseContext, viewerIdentityIds, t) {
  if (!seq) return [];

  const slots = [
    { slot: "A", playerId: seq.player_a_id, role: seq.player_a_role },
    { slot: "B", playerId: seq.player_b_id, role: seq.player_b_role },
    { slot: "C", playerId: seq.player_c_id, role: seq.player_c_role },
    { slot: "D", playerId: seq.player_d_id, role: seq.player_d_role },
  ];

  const seenIds = new Set();
  const lines = [];
  for (const entry of slots) {
    if (!entry.playerId) continue;
    const player = resolveExercisePlayerById(exerciseContext, entry.playerId);
    const ids = [entry.playerId, player?.player_id, player?.user_id]
      .filter(Boolean)
      .map(String);
    // Same person on A and B (player_id vs user_id mix) → one participant line.
    if (ids.some((id) => seenIds.has(id))) continue;
    ids.forEach((id) => seenIds.add(id));
    const roleLabel = formatParticipationRoleLabel(entry.role, t);
    const isViewer = viewerIdentityIds.some((id) => ids.includes(String(id)));
    lines.push({
      slot: entry.slot,
      name: formatExercisePlayerName(player, entry.playerId),
      role: roleLabel,
      isViewer,
      isActive: isActiveRole(entry.role),
      isSparring: isSparringRole(entry.role),
    });
  }
  return lines;
}

function buildAssignedPlayersRoleLines(
  players,
  exerciseContext,
  seq,
  viewerIdentityIds,
  t
) {
  if (!players?.length) return null;
  return players.map((entry) =>
    roleLineFromAssignedEntry(
      entry,
      exerciseContext,
      seq,
      viewerIdentityIds,
      t
    )
  );
}

function buildManualAssignmentRoleLines(
  msg,
  exerciseContext,
  assignment,
  seq,
  viewerIdentityIds,
  t
) {
  if (!assignment) return null;
  const players = resolveManualAssignmentPlayers({
    msg,
    assignment,
    exerciseContext,
    seq,
  });
  return buildAssignedPlayersRoleLines(
    players,
    exerciseContext,
    seq,
    viewerIdentityIds,
    t
  );
}

/** Exercise / session display name for Media card headers. */
export function resolveExerciseDisplayName(exercise, t) {
  if (!exercise) return null;
  return (
    exercise.ex_name ||
    exercise.name ||
    (exercise.type_ex && exercise.sub_type_ex
      ? `${exercise.type_ex.type_name_en} - ${exercise.sub_type_ex.sub_type_name_en}`
      : null) ||
    (exercise.te_order != null
      ? `${t("exercise", "Exercise")} #${exercise.te_order}`
      : null)
  );
}

/**
 * Same metadata object used by exercise-results Media tab cards.
 */
export function buildEvaluationVideoMetadata({
  msg,
  exercise,
  fullExercise,
  exName,
  sequences,
  viewerIdentityIds = [],
  localeCode = "en",
  t = (k, d) => d || k,
  videoIndex = null,
  kotMatches = null,
}) {
  const exerciseContext = buildExercisePlayerContext(exercise, fullExercise);
  const teOrder = exercise?.te_order || exerciseContext?.te_order;
  const resolvedExName =
    exName || resolveExerciseDisplayName(exercise || fullExercise, t);

  const kotMatch =
    Array.isArray(kotMatches) && kotMatches.length
      ? resolveKotMatchForFeedback(msg, kotMatches)
      : null;

  if (kotMatch) {
    const roundNumber = Number(kotMatch.round_number);
    const tableNumber = Number(kotMatch.table_number);
    const roundLabel =
      Number.isFinite(roundNumber) && roundNumber > 0
        ? `${t("round", "Round")} ${roundNumber}`
        : null;
    const tableLabel = formatExerciseTableLabel(
      Number.isFinite(tableNumber) && tableNumber > 0 ? tableNumber : null,
      t
    );
    const roleLines = buildKotMatchVideoRoleLines(
      kotMatch,
      viewerIdentityIds,
      t
    );

    const titleParts = [];
    if (teOrder) titleParts.push(`#${teOrder}`);
    if (resolvedExName) titleParts.push(resolvedExName);
    if (roundLabel) titleParts.push(roundLabel);
    if (tableLabel) titleParts.push(tableLabel);

    const exerciseLabel = [teOrder ? `#${teOrder}` : null, resolvedExName]
      .filter(Boolean)
      .join(" · ");

    return {
      title: titleParts.join(" · ") || t("evaluationVideo", "Evaluation Video"),
      exerciseLabel: exerciseLabel || null,
      teOrder: teOrder || null,
      exName: resolvedExName || null,
      seqOrder: null,
      subtype: null,
      roundNumber:
        Number.isFinite(roundNumber) && roundNumber > 0 ? roundNumber : null,
      roundLabel,
      roleLines,
      tableNumber:
        Number.isFinite(tableNumber) && tableNumber > 0 ? tableNumber : null,
      tableLabel,
      cameraPosition: null,
      cameraSetupLabel: formatCameraSetupLabelForMessage(msg, null, t),
      isLefty: String(msg?.sender_player_hand || "").toLowerCase() === "left",
      leftyLabel: t("leftyCameraBadge", "Lefty cam"),
      isKotMatch: true,
    };
  }

  const matchedSeq = findSequenceForFeedbackMsg(msg, sequences, videoIndex);
  const seqOrder =
    matchedSeq?.sequence_order ?? resolveFeedbackSequenceOrder(msg);
  const subtype = getSequenceSubtypeLabel(matchedSeq, localeCode);
  const manualAssignment = resolveManualVideoAssignment(msg);
  const manualRoleLines = buildManualAssignmentRoleLines(
    msg,
    exerciseContext,
    manualAssignment,
    matchedSeq,
    viewerIdentityIds,
    t
  );
  // Upload meta stamps assigned_players without assignment_manual — prefer that
  // over a stale sequence that can list the same person twice.
  const clipAssignedRoleLines = buildAssignedPlayersRoleLines(
    resolveClipAssignedPlayers(msg),
    exerciseContext,
    matchedSeq,
    viewerIdentityIds,
    t
  );
  const roleLines =
    manualRoleLines ||
    clipAssignedRoleLines ||
    buildSequencePlayerRoleLines(
      matchedSeq,
      exerciseContext,
      viewerIdentityIds,
      t
    );

  const cameraPosition = resolveEvaluationVideoCameraPosition(
    msg,
    matchedSeq,
    (seq, position) =>
      resolveSequenceSlotIdentityIds(seq, position, exerciseContext)
  );

  const tableFromManual = Number(manualAssignment?.tableNumber);
  const tableFromUrl = parseTableNumberFromVideoUrl(msg?.video_message_url);
  const tableFromSeq = Number(matchedSeq?.table_number);
  const tableFromExercise = Number(
    exerciseContext?.table_number ?? exercise?.table_number
  );
  const senderTable = resolveViewerTableNumber(
    exerciseContext,
    msg?.sender_id,
    msg?.sender_id
  );
  const tableNumber =
    (Number.isFinite(tableFromManual) &&
      tableFromManual > 0 &&
      tableFromManual) ||
    (Number.isFinite(tableFromUrl) && tableFromUrl > 0 && tableFromUrl) ||
    (Number.isFinite(tableFromSeq) && tableFromSeq > 0 && tableFromSeq) ||
    (Number.isFinite(Number(senderTable)) &&
      Number(senderTable) > 0 &&
      Number(senderTable)) ||
    (Number.isFinite(tableFromExercise) &&
      tableFromExercise > 0 &&
      tableFromExercise) ||
    null;

  const titleParts = [];
  if (teOrder) titleParts.push(`#${teOrder}`);
  if (resolvedExName) titleParts.push(resolvedExName);
  if (seqOrder > 0) {
    titleParts.push(`${t("sequence", "Sequence")} ${seqOrder}`);
  }
  if (subtype) titleParts.push(subtype);
  if (isStrokeSlowMoEvaluationVideo(msg)) {
    titleParts.push(t("strokeSlowMoShort", "Slow-mo reel"));
  }
  if (isMaxRallyEvaluationVideo(msg)) {
    titleParts.push(t("maxRallyReelShort", "Reel Max Rally"));
  }
  if (isExerciseInstructionEvaluationVideo(msg)) {
    titleParts.push(
      t("exerciseInstructionsShort", "Exercise instructions")
    );
  }

  const exerciseLabel = [teOrder ? `#${teOrder}` : null, resolvedExName]
    .filter(Boolean)
    .join(" · ");

  return {
    title: titleParts.join(" · ") || t("evaluationVideo", "Evaluation Video"),
    exerciseLabel: exerciseLabel || null,
    teOrder: teOrder || null,
    exName: resolvedExName || null,
    seqOrder,
    subtype,
    roundNumber: null,
    roundLabel: null,
    roleLines,
    tableNumber,
    tableLabel: formatExerciseTableLabel(tableNumber, t),
    cameraPosition: cameraPosition || null,
    cameraSetupLabel: formatCameraSetupLabelForMessage(msg, cameraPosition, t),
    isLefty: String(msg?.sender_player_hand || "").toLowerCase() === "left",
    leftyLabel: t("leftyCameraBadge", "Lefty cam"),
    isKotMatch: false,
  };
}

export function formatEvaluationVideoCardDate(msg, localeCode = "en") {
  const raw = resolveEvaluationVideoRecordingDate(msg);
  const dateObj = raw ? new Date(raw) : null;
  if (!dateObj || Number.isNaN(dateObj.getTime())) return "";
  const loc = localeCode === "fr" ? "fr-FR" : "en-US";
  return `${dateObj.toLocaleDateString(loc, {
    month: "short",
    day: "numeric",
  })} @ ${dateObj.toLocaleTimeString(loc, {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

export function formatEvalWallMs(ms) {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return null;
  const sec = ms / 1000;
  if (sec < 10) return `${sec.toFixed(1)}s`;
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function firstFiniteDateMs(...candidates) {
  for (const value of candidates) {
    if (value == null || value === "") continue;
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  return null;
}

/**
 * Wall time from upload start through browser-compat processing until playable.
 * Pose/stroke analysis is not included.
 */
export function resolveEvaluationVideoReadyWall(msg) {
  const setup = parseEvaluationViewSetup(msg?.view_setup);
  const playback =
    setup.web_playback && typeof setup.web_playback === "object"
      ? setup.web_playback
      : {};
  const status = String(playback.status || "").toLowerCase();
  const pending =
    status === "processing" ||
    status === "queued" ||
    status === "transcoding";

  const stored = Number(setup.ready_wall_ms ?? playback.ready_wall_ms);
  if (Number.isFinite(stored) && stored > 0) {
    return {
      wallMs: stored,
      pending: false,
      label: formatEvalWallMs(stored),
    };
  }

  const startMs = firstFiniteDateMs(
    setup.upload_started_at,
    setup.uploaded_at,
    playback.started_at,
    msg?.createdAt,
    msg?.created_at
  );
  const readyMs = firstFiniteDateMs(
    setup.ready_at,
    playback.finished_at,
    playback.updated_at
  );
  if (startMs != null && readyMs != null && readyMs >= startMs) {
    const wallMs = readyMs - startMs;
    if (wallMs > 0) {
      return {
        wallMs,
        pending: false,
        label: formatEvalWallMs(wallMs),
      };
    }
  }

  if (pending) {
    return { wallMs: null, pending: true, label: null };
  }
  return null;
}

